import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { formatVehicleType } from '../utils/formatters';
import { 
  Search, Loader2, Link2, Plus, MapPin, Truck, ChevronDown, 
  ChevronRight, Check, AlertCircle, ExternalLink
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OperationSelector - Composant pour sélectionner ou créer une opération mère
 * 
 * Props:
 * - contractId: ID du contrat actuel
 * - contractNumber: Numéro du contrat
 * - selectedOperationId: ID de l'opération sélectionnée
 * - onSelect: (operation) => void
 * - onModeChange: (mode: 'existing' | 'new') => void
 * - mode: 'existing' | 'new'
 * - departureAddress: Adresse de départ (pour nouvelle opération)
 * - arrivalAddress: Adresse d'arrivée (pour nouvelle opération)
 */
const OperationSelector = ({
  contractId,
  contractNumber,
  selectedOperationId,
  onSelect,
  onModeChange,
  mode = 'existing',
  departureAddress,
  arrivalAddress
}) => {
  const [loading, setLoading] = useState(false);
  const [linkedOperations, setLinkedOperations] = useState([]);
  const [externalOperations, setExternalOperations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showExternal, setShowExternal] = useState(false);
  const [linkingOperation, setLinkingOperation] = useState(null);

  // Charger les opérations
  const fetchOperations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/transport-requests`, {
        headers: { 'Authorization': 'Bearer cookie-auth' },
        params: { limit: 500 }
      });
      
      const allOperations = response.data?.requests || response.data || [];
      
      // Séparer les opérations liées au contrat et les externes
      const linked = allOperations.filter(op => 
        op.linkedContractId === contractId || 
        op.linkedContractNumber === contractNumber
      );
      
      const external = allOperations.filter(op => 
        !op.linkedContractId && 
        op.linkedContractNumber !== contractNumber &&
        op.status !== 'cancelled' &&
        op.status !== 'annulee'
      );
      
      setLinkedOperations(linked);
      setExternalOperations(external);
      
      // Si une seule opération liée et rien de sélectionné, la pré-sélectionner
      if (linked.length === 1 && !selectedOperationId && mode === 'existing') {
        onSelect(linked[0]);
      }
    } catch (error) {
      console.error('Erreur chargement opérations:', error);
      toast.error('Erreur lors du chargement des opérations');
    } finally {
      setLoading(false);
    }
  }, [contractId, contractNumber, selectedOperationId, mode, onSelect]);

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  // Lier une opération externe au contrat
  const linkExternalOperation = async (operation) => {
    setLinkingOperation(operation.id);
    try {
      await axios.patch(
        `${API}/transport-requests/${operation.id}`,
        { 
          linkedContractId: contractId,
          linkedContractNumber: contractNumber 
        },
        { headers: { 'Authorization': 'Bearer cookie-auth' }}
      );
      
      toast.success(`Opération ${operation.operationNumber} liée au contrat`);
      
      // Rafraîchir les listes
      await fetchOperations();
      
      // Sélectionner l'opération
      onSelect({ ...operation, linkedContractId: contractId });
      setShowExternal(false);
    } catch (error) {
      console.error('Erreur liaison opération:', error);
      toast.error('Erreur lors de la liaison de l\'opération');
    } finally {
      setLinkingOperation(null);
    }
  };

  // Filtrer les opérations externes
  const filteredExternal = externalOperations.filter(op => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      op.operationNumber?.toLowerCase().includes(search) ||
      op.departure?.toLowerCase().includes(search) ||
      op.arrival?.toLowerCase().includes(search) ||
      op.clientName?.toLowerCase().includes(search) ||
      (op.firstName + ' ' + op.lastName).toLowerCase().includes(search)
    );
  });

  // Composant pour afficher une opération
  const OperationCard = ({ operation, isLinked = false, isSelected = false, onSelectOp, onLink }) => (
    <div
      data-testid={`operation-card-${operation.id}`}
      className={cn(
        "p-3 border rounded-lg cursor-pointer transition-all",
        isSelected 
          ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" 
          : "hover:border-gray-300 hover:bg-gray-50"
      )}
      onClick={() => onSelectOp?.(operation)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-gray-600">
              {operation.operationNumber}
            </span>
            {isLinked && (
              <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                <Link2 className="w-3 h-3 mr-1" />
                Lié
              </Badge>
            )}
            {operation.childrenCount > 0 && (
              <Badge variant="outline" className="text-xs">
                {operation.childrenCount} transfert(s)
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-700 mt-1">
            <MapPin className="w-3 h-3 text-blue-500" />
            <span className="truncate">{operation.departure || 'N/A'}</span>
            <span className="text-gray-400">→</span>
            <MapPin className="w-3 h-3 text-emerald-500" />
            <span className="truncate">{operation.arrival || 'N/A'}</span>
          </div>
          {operation.vehicleType && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
              <Truck className="w-3 h-3" />
              <span>{formatVehicleType(operation.vehicleType)}</span>
            </div>
          )}
        </div>
        
        {isSelected && (
          <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        )}
        
        {!isLinked && onLink && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onLink(operation);
            }}
            disabled={linkingOperation === operation.id}
            className="flex-shrink-0 ml-2"
          >
            {linkingOperation === operation.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Link2 className="w-4 h-4 mr-1" />
                Lier
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="operation-selector">
      {/* Mode selector */}
      <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg" data-testid="operation-mode-selector">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={mode === 'existing'}
            onChange={() => onModeChange('existing')}
            className="w-4 h-4 text-emerald-600"
            data-testid="mode-existing-radio"
          />
          <span className="text-sm font-medium">Opération existante</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={mode === 'new'}
            onChange={() => onModeChange('new')}
            className="w-4 h-4 text-emerald-600"
            data-testid="mode-new-radio"
          />
          <span className="text-sm font-medium">Nouvelle opération</span>
        </label>
      </div>

      {mode === 'existing' && (
        <div className="space-y-4" data-testid="existing-operations-section">
          {/* Opérations liées au contrat */}
          <div data-testid="linked-operations-list">
            <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-emerald-600" />
              Opérations liées à ce contrat ({linkedOperations.length})
            </Label>
            
            {loading ? (
              <div className="flex items-center justify-center p-6 border rounded-lg bg-gray-50">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-500">Chargement...</span>
              </div>
            ) : linkedOperations.length > 0 ? (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {linkedOperations.map((op) => (
                  <OperationCard
                    key={op.id}
                    operation={op}
                    isLinked={true}
                    isSelected={selectedOperationId === op.id}
                    onSelectOp={onSelect}
                  />
                ))}
              </div>
            ) : (
              <div className="p-4 border border-dashed rounded-lg bg-gray-50 text-center text-gray-500 text-sm">
                <AlertCircle className="w-5 h-5 mx-auto mb-2 text-gray-400" />
                Aucune opération liée à ce contrat
                <p className="text-xs text-gray-400 mt-1">
                  Créez une nouvelle opération ou liez une opération existante
                </p>
              </div>
            )}
          </div>

          {/* Section opérations externes */}
          <div data-testid="external-operations-section">
            <button
              onClick={() => setShowExternal(!showExternal)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
              data-testid="toggle-external-operations"
            >
              {showExternal ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              <ExternalLink className="w-4 h-4" />
              <span>Lier une opération externe ({externalOperations.length} disponibles)</span>
            </button>
            
            {showExternal && (
              <div className="mt-3 p-3 border rounded-lg bg-blue-50 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher par numéro, trajet, client..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-white"
                  />
                </div>
                
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {filteredExternal.length > 0 ? (
                    filteredExternal.slice(0, 20).map((op) => (
                      <OperationCard
                        key={op.id}
                        operation={op}
                        isLinked={false}
                        isSelected={selectedOperationId === op.id}
                        onSelectOp={(op) => {
                          onSelect(op);
                          // Proposer de lier automatiquement
                        }}
                        onLink={linkExternalOperation}
                      />
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      Aucune opération externe trouvée
                    </div>
                  )}
                </div>
                
                {filteredExternal.length > 20 && (
                  <p className="text-xs text-gray-500 text-center">
                    {filteredExternal.length - 20} autres opérations non affichées. Utilisez la recherche.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {mode === 'new' && (
        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-start gap-3">
            <Plus className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800 font-medium">
                Nouvelle opération
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Une nouvelle opération sera créée automatiquement avec le trajet défini ci-dessous.
                Elle sera liée à ce contrat.
              </p>
              {departureAddress && arrivalAddress && (
                <div className="mt-2 p-2 bg-white rounded border border-blue-200">
                  <div className="flex items-center gap-1 text-xs">
                    <MapPin className="w-3 h-3 text-blue-500" />
                    <span className="truncate">{departureAddress}</span>
                    <span className="text-gray-400 mx-1">→</span>
                    <MapPin className="w-3 h-3 text-emerald-500" />
                    <span className="truncate">{arrivalAddress}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationSelector;
